import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnDestroy, AfterViewInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FikoYTService } from '@/src/service/fikoyt.service';
import * as THREE from 'three';
import { FikoIsikYonsel } from '../../FikoIsikYonsel';

declare var $: any;

@Component({
  selector: 'fikoyt-fiko-isik-yonsel',
  templateUrl: './fiko-isik-yonsel.component.html',
  styleUrls: ['./fiko-isik-yonsel.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule]
})
export class FikoIsikYonselComponent implements AfterViewInit, OnDestroy {
  id_dialog = "dialogFikoIsikYonsel" + this.FikoYT.fikoIdUret();
  subsList: Subscription[] = [];

  // Global fonksiyonlar template'de erişim için
  parseFloat = parseFloat;

  @Input() nesne: FikoIsikYonsel;
  @Input() pencere: any; // Dialog Core'dan gelen pencere referansı

  constructor(public readonly FikoYT: FikoYTService, public readonly CD: ChangeDetectorRef) {
    this.subsList.push(FikoYT.degisti.subscribe(() => {
      this.CD.detectChanges();
    }));
  }

  ngAfterViewInit(): void {
    // Başlangıçta sabit pozisyonu ayarla (nesne input'u geldikten sonra)
    if (this.nesne) {
      this.sabitPozisyon.copy(this.nesne.position);
    }
  }

  renkDegisti($event: any) {
   this.nesne.color.set(new THREE.Color($event));
   this.nesne.sahne.anaGosterim.ciz();
  }

  // Sabit pozisyonu sakla (kamera takip kapalıyken kullanılacak)
  private sabitPozisyon: THREE.Vector3 = new THREE.Vector3();

  // Pre-allocated buffers for zero-allocation operations
  private _tmpVec3A: THREE.Vector3 = new THREE.Vector3();
  private _tmpVec3B: THREE.Vector3 = new THREE.Vector3();
  private _tmpVec3C: THREE.Vector3 = new THREE.Vector3();
  private _tmpMat4: THREE.Matrix4 = new THREE.Matrix4();
  private _tmpQuat: THREE.Quaternion = new THREE.Quaternion();

  // Kamera Takip Ayarları
  toggleKameraTakip() {
    this.nesne.kameraTakipAktif = !this.nesne.kameraTakipAktif;

    if (this.nesne.kameraTakipAktif) {
      // Kamera takip aktif edildiğinde mevcut pozisyonu sabit olarak kaydet
      this.sabitPozisyon.copy(this.nesne.position);

      // Mevcut pozisyonu kamera pozisyonundan çıkararak offset'i hesapla
      const camera = this.nesne.sahne?.anaGosterim?.aktifKamera;
      if (camera) {
        // Mevcut pozisyondan kamera pozisyonunu çıkararak manuel offset'i bul (zero-allocation)
        this._tmpVec3A.copy(this.nesne.position).sub(camera.position);
        // Kamera yönünü hesaba kat (zero-allocation)
        this._tmpVec3B.set(0, 0, -1);
        this._tmpVec3B.applyQuaternion(camera.quaternion);
        // Offset'i kamera koordinat sistemine dönüştür (zero-allocation)
        this._tmpQuat.copy(camera.quaternion).invert();
        this._tmpMat4.makeRotationFromQuaternion(this._tmpQuat);
        this.nesne.kameraManuelOffset.copy(this._tmpVec3A.applyMatrix4(this._tmpMat4));
      } else {
        // Kamera yoksa mevcut pozisyonu doğrudan offset olarak kullan
        this.nesne.kameraManuelOffset.copy(this.nesne.position);
      }
      this.nesne.updateLightPosition();
    } else {
      // Kamera takip kapatıldığında son pozisyonu sabitle
      const camera = this.nesne.sahne?.anaGosterim?.aktifKamera;
      if (camera) {
        // Zero-allocation: use pre-allocated buffers
        this._tmpVec3A.set(0, 0, this.nesne.kameraOffsetUzakligi);
        this._tmpVec3A.applyQuaternion(camera.quaternion);
        // Son pozisyonu sabit pozisyon olarak kaydet
        this.sabitPozisyon.copy(camera.position).add(this._tmpVec3A).add(this.nesne.kameraManuelOffset);
      }
      // Işığı sabit pozisyona ayarla
      this.nesne.position.copy(this.sabitPozisyon);

      // Target'ı merkeze ayarla (zero-allocation)
      this.nesne.target.position.set(0, 0, 0);
      this.nesne.target.updateMatrixWorld();
      this.nesne.updateMatrixWorld();
    }

    this.nesne.sahne.anaGosterim.ciz();
  }

  setKameraOffsetUzakligi(deger: number) {
    this.nesne.kameraOffsetUzakligi = deger;

    if (this.nesne.kameraTakipAktif) {
      // Aktif ise pozisyonu güncelle
      this.nesne.updateLightPosition();
    }

    this.nesne.sahne.anaGosterim.ciz();
  }

  // Manuel konum değişikliği (X, Y, Z slider'ları için)
  manuelKonumDegisti(eksen: 'x' | 'y' | 'z', deger: number) {
    if (this.nesne.kameraTakipAktif) {
      // Kamera takip modundayken manuel offset'i güncelle
      this.nesne.kameraManuelOffset[eksen] = deger;
      this.nesne.updateLightPosition();
    } else {
      // Manuel modda doğrudan pozisyonu güncelle
      this.nesne.position[eksen] = deger;
    }

    this.nesne.sahne.anaGosterim.ciz();
  }

  // Tekil offset'i sıfırla
  sifirlaOffset(eksen: 'x' | 'y' | 'z') {
    if (this.nesne.kameraTakipAktif) {
      this.nesne.kameraManuelOffset[eksen] = 0;
      this.nesne.updateLightPosition();
    } else {
      this.nesne.position[eksen] = 0;
    }
    this.nesne.sahne.anaGosterim.ciz();
  }

  // Tüm offset'leri sıfırla
  sifirlaTumOffset() {
    if (this.nesne.kameraTakipAktif) {
      this.nesne.kameraManuelOffset.set(0, 0, 0);
      this.nesne.updateLightPosition();
    } else {
      this.nesne.position.set(0, 0, 0);
    }
    this.nesne.sahne.anaGosterim.ciz();
  }

  // Kamera offset'ini sıfırla
  sifirlaKameraOffset() {
    this.nesne.kameraOffsetUzakligi = 20; // Varsayılan değere dön
    if (this.nesne.kameraTakipAktif) {
      this.nesne.updateLightPosition();
    }
    this.nesne.sahne.anaGosterim.ciz();
  }

  // CAD Gölge Ayarları
  setShadowMapSize(size: number) {
    this.nesne.shadow.mapSize.width = size;
    this.nesne.shadow.mapSize.height = size;
    this.nesne.shadow.map?.dispose();
    this.nesne.shadow.map = null;
    this.nesne.sahne.anaGosterim.ciz();
  }

  setShadowCameraArea(area: number) {
    this.nesne.shadow.camera.left = -area;
    this.nesne.shadow.camera.right = area;
    this.nesne.shadow.camera.top = area;
    this.nesne.shadow.camera.bottom = -area;
    this.nesne.shadow.camera.updateProjectionMatrix();
    this.nesne.sahne.anaGosterim.ciz();
  }

  setShadowCameraFar(far: number) {
    this.nesne.shadow.camera.far = far;
    this.nesne.shadow.camera.updateProjectionMatrix();
    this.nesne.sahne.anaGosterim.ciz();
  }

  public kapa() {
    // Dialog core'ın kapa metodunu çağır
    // FikoDialogCore'dan { instance: this } şeklinde gelir
    if (this.pencere?.instance && typeof this.pencere.instance.kapa === 'function') {
      try {
        this.pencere.instance.kapa();
        return;
      } catch (error) {
        // pencere.instance.kapa() hata
      }
    }

    // Fallback: direkt pencere.kapa() dene (eski pattern için)
    if (this.pencere && typeof this.pencere.kapa === 'function') {
      try {
        this.pencere.kapa();
        return;
      } catch (error) {
        // pencere.kapa() hata
      }
    }
  }

  ngOnDestroy(): void {
    $("#" + this.id_dialog).remove();
    this.subsList.forEach(element => {
      element.unsubscribe();
    });
  }
}
